import { useState, useEffect, useCallback } from 'react';
import { 
  Upload, FileText, Link2, Eye, EyeOff, Trash2, RefreshCw, 
  Copy, ExternalLink, BarChart2, Clock, Download, Lock,
  MoreVertical, Search, Filter, Plus, X, Check, Loader2,
  FileImage, Presentation, Calendar, Globe, Pause, Play
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

export default function SharedDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [copiedId, setCopiedId] = useState(null);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: '',
    description: '',
    tags: '',
    allowDownload: false,
    isPasswordProtected: false,
    password: '',
    expiresAt: ''
  });

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter
      });
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API_URL}/shared-documents?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setDocuments(response.data.documents);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, searchQuery]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('document', uploadForm.file);
      formData.append('title', uploadForm.title || uploadForm.file.name.replace(/\.[^/.]+$/, ''));
      formData.append('description', uploadForm.description);
      formData.append('tags', uploadForm.tags);
      formData.append('allowDownload', uploadForm.allowDownload);
      formData.append('isPasswordProtected', uploadForm.isPasswordProtected);
      if (uploadForm.isPasswordProtected) {
        formData.append('password', uploadForm.password);
      }
      if (uploadForm.expiresAt) {
        formData.append('expiresAt', uploadForm.expiresAt);
      }

      await axios.post(`${API_URL}/shared-documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setShowUploadModal(false);
      setUploadForm({
        file: null,
        title: '',
        description: '',
        tags: '',
        allowDownload: false,
        isPasswordProtected: false,
        password: '',
        expiresAt: ''
      });
      fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleStatusChange = async (docId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/shared-documents/${docId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      fetchDocuments();
    } catch (error) {
      console.error('Status change error:', error);
    }
  };

  const handleRegenerateUrl = async (docId) => {
    if (!confirm('This will invalidate the current share URL. Continue?')) return;
    
    try {
      const response = await axios.post(`${API_URL}/shared-documents/${docId}/regenerate-url`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setDocuments(docs => docs.map(d => 
        d._id === docId ? { 
          ...d, 
          shareSlug: response.data.shareSlug, 
          shareUrl: response.data.shareUrl,
          expiresAt: response.data.expiresAt,
          status: response.data.status || 'active'
        } : d
      ));
    } catch (error) {
      console.error('Regenerate URL error:', error);
    }
  };

  const handleReconvert = async (docId) => {
    try {
      await axios.post(`${API_URL}/shared-documents/${docId}/reconvert`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchDocuments();
    } catch (error) {
      console.error('Reconvert error:', error);
    }
  };

  const handleDelete = async (docId, permanent = false) => {
    const message = permanent 
      ? 'Permanently delete this document? This cannot be undone.'
      : 'Delete this document?';
    if (!confirm(message)) return;

    try {
      await axios.delete(`${API_URL}/shared-documents/${docId}${permanent ? '?permanent=true' : ''}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchDocuments();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleViewAnalytics = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/shared-documents/${doc._id}/analytics`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAnalytics(response.data);
      setSelectedDocument(doc);
      setShowAnalyticsModal(true);
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const getDocTypeIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
      case 'ppt':
      case 'pptx': return <Presentation className="w-5 h-5 text-orange-500" />;
      case 'html': return <Globe className="w-5 h-5 text-green-500" />;
      default: return <FileImage className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusBadge = (status, expiresAt) => {
    // Check if document is expired
    const isExpired = expiresAt && new Date(expiresAt) < new Date();
    
    const styles = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      deleted: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      expired: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
    };
    
    const displayStatus = isExpired ? 'expired' : status;
    const displayLabel = isExpired ? 'Expired' : status.charAt(0).toUpperCase() + status.slice(1);
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[displayStatus] || styles.active}`}>
        {displayLabel}
      </span>
    );
  };

  const getConversionBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      pending: 'bg-gray-100 text-gray-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Shared Documents</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Host and share PDF, PPT, PPTX files as web pages
          </p>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Upload Document
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
        </select>
      </div>

      {/* Documents Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No documents yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Upload your first PDF or presentation to share as a web page
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Upload className="w-5 h-5" />
            Upload Document
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {documents.map((doc) => (
            <div
              key={doc._id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  {getDocTypeIcon(doc.documentType)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                      {doc.title}
                    </h3>
                    {getStatusBadge(doc.status, doc.expiresAt)}
                    {doc.isPasswordProtected && (
                      <Lock className="w-4 h-4 text-gray-400" title="Password protected" />
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-1">
                    {doc.description || 'No description'}
                  </p>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {format(new Date(doc.createdAt), 'MMM d, yyyy')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {doc.analytics?.totalViews || 0} views
                    </span>
                    <span className="flex items-center gap-1">
                      <FileText className="w-4 h-4" />
                      {doc.webView?.totalPages || 0} pages
                    </span>
                    {getConversionBadge(doc.webView?.conversionStatus)}
                  </div>

                  {/* Share URL */}
                  {doc.shareUrl && (
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm">
                        <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate text-gray-600 dark:text-gray-300">{doc.shareUrl}</span>
                      </div>
                      <button
                        onClick={() => copyToClipboard(doc.shareUrl, doc._id)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Copy URL"
                      >
                        {copiedId === doc._id ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                      <a
                        href={doc.shareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Open in new tab"
                      >
                        <ExternalLink className="w-4 h-4 text-gray-500" />
                      </a>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {doc.status === 'active' ? (
                    <button
                      onClick={() => handleStatusChange(doc._id, 'paused')}
                      className="p-2 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded-lg transition-colors"
                      title="Pause sharing"
                    >
                      <Pause className="w-5 h-5 text-yellow-600" />
                    </button>
                  ) : doc.status === 'paused' ? (
                    <button
                      onClick={() => handleStatusChange(doc._id, 'active')}
                      className="p-2 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                      title="Resume sharing"
                    >
                      <Play className="w-5 h-5 text-green-600" />
                    </button>
                  ) : null}
                  
                  <button
                    onClick={() => handleViewAnalytics(doc)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="View analytics"
                  >
                    <BarChart2 className="w-5 h-5 text-gray-500" />
                  </button>
                  
                  <button
                    onClick={() => handleRegenerateUrl(doc._id)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Regenerate URL"
                  >
                    <RefreshCw className="w-5 h-5 text-gray-500" />
                  </button>

                  {doc.webView?.conversionStatus === 'failed' && (
                    <button
                      onClick={() => handleReconvert(doc._id)}
                      className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      title="Retry conversion"
                    >
                      <RefreshCw className="w-5 h-5 text-blue-500" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleDelete(doc._id)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setPagination(prev => ({ ...prev, page }))}
              className={`px-3 py-1 rounded ${
                pagination.page === page
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Document</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpload} className="p-4 space-y-4">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Document File *
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                  {uploadForm.file ? (
                    <div className="flex items-center justify-center gap-2">
                      {getDocTypeIcon(uploadForm.file.name.split('.').pop())}
                      <span className="text-gray-700 dark:text-gray-300">{uploadForm.file.name}</span>
                      <button
                        type="button"
                        onClick={() => setUploadForm(prev => ({ ...prev, file: null }))}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600 dark:text-gray-400">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-sm text-gray-500 mt-1">PDF, PPT, PPTX, HTML (max 100MB)</p>
                      <input
                        type="file"
                        accept=".pdf,.ppt,.pptx,.html,.htm"
                        onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files[0] }))}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Document title (optional)"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description (optional)"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="Comma-separated tags"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Options */}
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={uploadForm.allowDownload}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, allowDownload: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Allow download</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={uploadForm.isPasswordProtected}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, isPasswordProtected: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Password protect</span>
                </label>

                {uploadForm.isPasswordProtected && (
                  <input
                    type="password"
                    value={uploadForm.password}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter password"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                )}
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiration Date (optional)
                </label>
                <input
                  type="datetime-local"
                  value={uploadForm.expiresAt}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!uploadForm.file || uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload & Share
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && analytics && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Analytics: {analytics.title}
              </h2>
              <button
                onClick={() => setShowAnalyticsModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-primary-600">{analytics.totalViews}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Views</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{analytics.uniqueViews}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Unique Views</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{analytics.viewsLast7Days}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Last 7 Days</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{analytics.viewsLast30Days}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Last 30 Days</p>
                </div>
              </div>

              {/* Last Viewed */}
              {analytics.lastViewedAt && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Last viewed: {format(new Date(analytics.lastViewedAt), 'MMM d, yyyy h:mm a')}
                </p>
              )}

              {/* Recent Views */}
              {analytics.recentViews?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Views</h3>
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600">
                        <tr>
                          <th className="text-left py-2">Time</th>
                          <th className="text-left py-2">IP Address</th>
                          <th className="text-left py-2">Location</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {analytics.recentViews.map((view, idx) => (
                          <tr key={idx} className="text-gray-600 dark:text-gray-400">
                            <td className="py-2">{format(new Date(view.viewedAt), 'MMM d, h:mm a')}</td>
                            <td className="py-2 font-mono text-xs">{view.ipAddress || 'N/A'}</td>
                            <td className="py-2">
                              {view.city || view.country ? (
                                <span>
                                  {view.city}{view.city && view.countryCode ? ', ' : ''}{view.countryCode}
                                </span>
                              ) : (
                                <span className="text-gray-400">Unknown</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
